import React, { FC, useMemo, useState } from "react";
import {
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  Typography,
  IconButton,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useTranslation } from "react-i18next";
import { INPUT_TEXT_TYPE } from "./InputText";

export type TextFieldProps = {
  errorEnabled?: boolean;
  label?: string;
  isLabelDefaultBehaviour?: boolean;
  secureTextEntry?: boolean;
  labelSx?: any;
  fieldValue?: any;
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errorMessage?: string;
  isConfirmPassword?: boolean;
  isRequired?: boolean;
  readOnly?: boolean;
} & MuiTextFieldProps;

const TextField: FC<TextFieldProps> = ({
  label,
  style,
  errorEnabled,
  isLabelDefaultBehaviour = false,
  sx,
  labelSx,
  type = "text",
  secureTextEntry = false,
  slotProps,
  fieldValue,
  handleChange,
  errorMessage,
  isConfirmPassword = false,
  multiline = false,
  isRequired = false,
  readOnly = false,
  ...rest
}) => {
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);

  const hideLabel = !isLabelDefaultBehaviour;

  const isPasswordInput = useMemo(
    () => type === INPUT_TEXT_TYPE.PASSWORD,
    [type]
  );

  const {
    borderWidth = 1,
    borderColor = "custom.border.default",
    borderRadius = 1,
    ...restInputSx
  } = typeof sx === "object" && sx !== null
      ? (sx as {
        borderWidth?: number;
        borderColor?: string;
        borderRadius?: number;
      })
      : {};

  const getInputSx = () => ({
    mb: 3,
    borderWidth,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: errorEnabled ? "custom.border.attention" : borderColor,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: errorEnabled ? "custom.border.attention" : borderColor,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderWidth: borderWidth,
      borderColor: errorEnabled ? "custom.border.attention" : borderColor,
    },
    ...restInputSx,
  });

  const getInputSlotProps = () => ({
    input: {
      sx: {
        height: multiline ? "auto" : 56,
        borderRadius: borderRadius,
        backgroundColor: "transparent",
      },
      endAdornment: isPasswordInput ? (
        <IconButton
          aria-label={
            showPassword ? "hide the password" : "display the password"
          }
          onClick={() => setShowPassword((prev) => !prev)}
          edge="end"
        >
          {!showPassword ? (
            <VisibilityOutlinedIcon />
          ) : (
            <VisibilityOffOutlinedIcon />
          )}
        </IconButton>
      ) : null,

      ...slotProps?.input,
    },
    inputLabel: {
      sx: {
        color: "custom.foreground.primary",
        ...labelSx,
        "&.Mui-focused": {
          color: "custom.foreground.primary",
        },
      },
      ...slotProps?.inputLabel,
    },
  });

  const inputType = useMemo(() => {
    if (secureTextEntry) {
      return "password";
    }
    if (isPasswordInput) {
      return !showPassword ? "password" : "text";
    }
    if (type === INPUT_TEXT_TYPE.NUMBER) {
      return "text";
    }
    return type;
  }, [secureTextEntry, isPasswordInput, showPassword, type]);

  const FinalLabel = isRequired ? (
    <span>{label}<span style={{ color: "red" }}>*</span></span>
  ) : (
    label
  );

  return (
    <>
      {!isLabelDefaultBehaviour && <Typography mb={0.5}>{label}</Typography>}

      <MuiTextField
        variant="outlined"
        type={inputType}
        value={fieldValue}
        disabled={readOnly}
        onChange={handleChange}
        error={errorEnabled}
        color={errorEnabled ? "error" : "primary"}
        label={hideLabel ? "" : FinalLabel}
        helperText={t(errorMessage ?? "")}
        fullWidth
        onPaste={(e) => {
          if (isConfirmPassword) {
            e.preventDefault();
          }
        }}
        slotProps={getInputSlotProps()}
        multiline={multiline}
        {...rest}
        sx={getInputSx()}
      />
    </>
  );
};

export default TextField;
